import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ExternalLink, Phone, Search, Landmark, ArrowLeft, History } from "lucide-react";

const CATEGORY_LABELS = {
  general: "General", civic: "Civic", police: "Police", consumer: "Consumer",
  cyber: "Cybercrime", rti: "RTI", women: "Women & Child", corruption: "Corruption",
};

// Rough map of country codes → likely current-country guess (used only when the user
// hasn't set an explicit filter and we know their city).
const CITY_COUNTRY_HINTS = {
  Lucknow: "IN", Delhi: "IN", Mumbai: "IN", Kolkata: "IN", Chennai: "IN",
  Bengaluru: "IN", Hyderabad: "IN", Pune: "IN", Ahmedabad: "IN", Jaipur: "IN",
  London: "GB", "New York": "US", Singapore: "SG", Dubai: "AE", Sydney: "AU",
  Toronto: "CA", Auckland: "NZ",
};

export default function CivicPortals() {
  const { user } = useAuth();
  const { loc } = useUserLocation();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [portals, setPortals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [f, setF] = useState({ q: "", country: "", state: "", category: "" });

  // Auto-suggest country by GPS-detected city on first mount
  useEffect(() => {
    if (!f.country && loc?.city && CITY_COUNTRY_HINTS[loc.city]) {
      setF((x) => ({ ...x, country: CITY_COUNTRY_HINTS[loc.city] }));
    }
  }, [loc?.city, f.country]);

  useEffect(() => { api.get("/civic-portals/countries").then(({ data }) => setCountries(data)).catch(() => {}); }, []);

  useEffect(() => {
    let ignore = false;
    setBusy(true);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/civic-portals", { params }).then(({ data }) => { if (!ignore) setPortals(data); })
      .catch(() => { if (!ignore) setPortals([]); })
      .finally(() => { if (!ignore) setBusy(false); });
    return () => { ignore = true; };
  }, [f]);

  useEffect(() => {
    if (user) api.get("/civic-portals/my/history").then(({ data }) => setHistory(data)).catch(() => {});
  }, [user]);

  const openPortal = async (portal) => {
    // Fire-and-forget click tracking (only if user is logged in — 401 is fine)
    if (user) {
      api.post(`/civic-portals/${portal.portal_id}/track`, { note: f.q || "" })
        .then(() => setHistory((h) => [{ portal_id: portal.portal_id, portal_name: portal.name, portal_url: portal.url, created_at: new Date().toISOString() }, ...h].slice(0, 20)))
        .catch(() => {});
    }
    window.open(portal.url, "_blank", "noopener,noreferrer");
    toast.success(`Opening ${portal.name}`);
  };

  const grouped = useMemo(() => {
    const m = new Map();
    for (const p of portals) {
      const key = p.state ? `${p.country} · ${p.state}` : `${p.country} · National`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(p);
    }
    return Array.from(m.entries());
  }, [portals]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6" data-testid="civic-portals-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="civic-back"><ArrowLeft size={16} className="mr-1" /> Back</Button>
        <div>
          <h1 className="heading text-2xl font-extrabold flex items-center gap-2"><Landmark className="text-[#E05E36]" /> Government Grievance Portals</h1>
          <div className="text-sm text-stone-600">Curated directory of official complaint portals — file, escalate and track your grievances with the right authority.</div>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search size={16} className="absolute left-3 top-3 text-stone-400" />
            <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search: 'women', 'RTI', 'cyber'…" className="pl-9" data-testid="civic-search-q" />
          </div>
          <Select value={f.country || "__all__"} onValueChange={(v) => setF({ ...f, country: v === "__all__" ? "" : v })}>
            <SelectTrigger data-testid="civic-filter-country"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any country</SelectItem>
              {countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.name} ({c.portal_count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.category || "__all__"} onValueChange={(v) => setF({ ...f, category: v === "__all__" ? "" : v })}>
            <SelectTrigger data-testid="civic-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {busy && <div className="text-sm text-stone-500">Loading portals…</div>}
      {!busy && portals.length === 0 && (
        <div className="text-center py-16 text-stone-500" data-testid="civic-empty">No portals match your filters. Try broadening them.</div>
      )}

      {grouped.map(([groupKey, group]) => (
        <div key={groupKey} className="space-y-2">
          <h3 className="text-xs uppercase tracking-widest font-bold text-stone-500">{groupKey}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {group.map((p) => (
              <Card key={p.portal_id} className="border-stone-200 hover:border-[#E05E36] transition-colors" data-testid={`civic-portal-${p.portal_id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-bold">{p.name}</CardTitle>
                  <div className="text-xs text-stone-500">{p.authority}</div>
                </CardHeader>
                <CardContent className="space-y-2 pb-3">
                  <div className="flex flex-wrap gap-1">
                    {(p.categories || []).map((c) => <Badge key={c} variant="secondary" className="text-xs">{CATEGORY_LABELS[c] || c}</Badge>)}
                    {(p.languages || []).map((l) => <Badge key={`lang-${l}`} variant="outline" className="text-xs uppercase">{l}</Badge>)}
                  </div>
                  {p.notes && <p className="text-xs text-stone-600">{p.notes}</p>}
                  <div className="flex items-center gap-3 text-xs text-stone-500 mt-2">
                    {p.helpline && <span className="flex items-center gap-1"><Phone size={12} /> <a href={`tel:${p.helpline}`} className="hover:underline">{p.helpline}</a></span>}
                  </div>
                  <div className="pt-2">
                    <Button size="sm" onClick={() => openPortal(p)} className="rounded-full gap-2 bg-[#E05E36] hover:bg-[#c94e2a]" data-testid={`civic-open-${p.portal_id}`}>
                      <ExternalLink size={14} /> Open portal
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {user && history.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><History size={16} /> Your recent filings</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            {history.slice(0, 5).map((h, i) => (
              <div key={i} className="flex items-center justify-between border-b border-stone-100 py-1" data-testid={`civic-history-${i}`}>
                <span className="truncate">{h.portal_name}</span>
                <span className="text-xs text-stone-400">{new Date(h.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
